import { takeLatest, call, put, take, race } from 'redux-saga/effects';
import { eventChannel } from 'redux-saga';
import {
    doAskCopilotSuccess, doAskCopilotFail,
	startAgentStreaming, appendAgentToken, attachAgentArtifact, finishAgentStreaming,
	failAgentStreaming,
	doLoadChatHistorySuccess, doLoadChatHistoryFail,
	doLoadConversationsSuccess, doLoadConversationsFail,
	doOpenConversationSuccess, doOpenConversationFail,
	BUBBLE_AGENT_KEYS,
} from './copilotState';

import createCrudService from '../../services/crudservice';
import createAIAgentService from '../../services/aiagentservice';
import { 
	doCrudCall, LOADING_MSG
} from '../../utils/crudSagaHelper';

const crudService = createCrudService('api/v1/copilot');

const aiAgentService = createAIAgentService();

function* doAskCopilot(data) {
	yield doCrudCall(
		() => crudService.doPostRequest(`general-accountant`, data.payload), 
		doAskCopilotSuccess,
		doAskCopilotFail, 
		null, null, 'Thinking...'
	);
}

// Bridges aiAgentService.streamChat's callback API (onDelta/onDone/onFallback/onError)
// into an eventChannel so the saga worker can `take` stream events one at a time.
// No eventChannel precedent existed in this repo before this task; it is the
// standard redux-saga primitive for callback-to-action bridging.
//
// FIX C2/I5: an AbortController is created here (not inside aiagentservice.js)
// so that whenever this channel is closed - stream finished normally, OR the
// consuming task was cancelled because the user switched threads, by clicking
// "New chat" or by opening a conversation from the sidebar (see doAgentChat
// below) - the underlying fetch is aborted instead of continuing in the
// background.
function createChatStreamChannel(payload) {
	let closed = false;
	const controller = new AbortController();
	return eventChannel((emit) => {
		aiAgentService.streamChat(payload, {
			onDelta: (delta) => { if (!closed) emit({ kind: 'delta', delta }); },
			onArtifact: (artifact) => { if (!closed) emit({ kind: 'artifact', artifact }); },
			onDone: () => { if (!closed) emit({ kind: 'done' }); },
			onFallback: () => { if (!closed) emit({ kind: 'fallback' }); },
			onError: (error) => { if (!closed) emit({ kind: 'error', error }); },
		}, controller.signal);
		return () => { closed = true; controller.abort(); };
	});
}

// Consumes the stream channel to completion (delta/artifact/done/failure). Runs
// inside a `race` (see doAgentChat) against the thread-switch dispatches: when
// redux-saga cancels the losing race branch, this generator's `finally` still runs,
// closing the channel - which aborts the in-flight fetch via
// createChatStreamChannel's unsubscribe - and no further token/success
// actions are dispatched because the generator is torn down mid-yield.
export function* consumeChatStream(channel, payload) {
	try {
		while (true) {
			const event = yield take(channel);
			if (event.kind === 'delta') {
				yield put(appendAgentToken(event.delta));
			} else if (event.kind === 'artifact') {
				yield put(attachAgentArtifact(event.artifact));
			} else if (event.kind === 'done') {
				yield put(finishAgentStreaming());
				return;
			} else {
				// 'fallback' or 'error'. BUG-004: this used to re-send the message
				// to the HR assistant, skipping every permission check. A stream
				// failure must never produce another agent's answer, so it ends
				// here with an error bubble the user can retry from.
				yield put(failAgentStreaming(payload));
				return;
			}
		}
	} finally {
		channel.close();
	}
}

function* doAgentChat(data) {
	// Empty loading message: do NOT trigger the global full-screen overlay for
	// chat. The bubble shows a contained "thinking" state via chatData.requesting.
	const payload = data.payload;
	yield put(startAgentStreaming());
	const channel = createChatStreamChannel(payload);
	// FIX C2/I5: race the stream consumption against every action that switches
	// which conversation is on screen, so a send still in flight is cancelled
	// instead of finishing into whatever thread replaced it. redux-saga
	// automatically cancels the losing race branch, which runs
	// consumeChatStream's `finally` (closing/aborting the channel) and stops
	// any further put()s from that task.
	//
	// FINDING (whole-branch review): doNewChat used to be the only entry here.
	// Opening a conversation ran on its own watcher and cancelled nothing, so
	// clicking a sidebar row mid-generation swapped the transcript out from under
	// a live turn. The success reducer replaced results with the other thread's
	// messages, none of which carry data.streaming, so the very next token pushed
	// a NEW agent bubble into the WRONG conversation and took the artifact
	// download button with it, while the server had stored the turn against the
	// original thread. Both actions replace the transcript, so both must cancel.
	yield race({
		stream: call(consumeChatStream, channel, payload),
		cancelledByThreadSwitch: take(['copilot/doNewChat', 'copilot/doOpenConversation']),
	});
}

// FINDING (whole-branch review): this asked for the newest conversation of ANY
// agent, while the full page's sidebar lists recruitment ones only. Real HR
// conversations exist (the 2026_08_05 backfill sets each thread's agent key from
// its newest message), so a user whose newest thread was an HR one had the bubble
// restore a conversation the sidebar could not list and no row could highlight,
// and the next message typed appended a recruitment message to it, which made the
// backend's tracker rewrite the thread's agent key and convert it. Passing the key
// is what originally made the bubble and the page agree on which conversations exist.
//
// M11: the bubble now routes through Eli, so its history has to widen rather than
// stay pinned to recruitment_partner alone. BUBBLE_AGENT_KEYS asks for eli AND
// recruitment_partner, so threads that predate the router stay visible next to
// Eli's new ones without being rewritten. The full page in views/console/eli-chat
// asks for the SAME pair, so both surfaces show the same set: a conversation is
// never visible on one and missing on the other.
//
// Only the CALLER changed. getChatHistory() with no arguments still builds the
// bare URL, which aiagentservice.test.js pins and the backend tests rely on.
function* doLoadChatHistory() {
	yield doCrudCall(
		() => aiAgentService.getChatHistory({ agentKey: BUBBLE_AGENT_KEYS }),
		doLoadChatHistorySuccess,
		doLoadChatHistoryFail,
		null, null, ''
	);
}

function* doLoadConversationsSaga(action) {
	yield doCrudCall(
		() => aiAgentService.getConversations(action.payload ?? {}),
		doLoadConversationsSuccess,
		doLoadConversationsFail,
		null, null, ''
	);
}

function* doOpenConversationSaga(action) {
	yield doCrudCall(
		() => aiAgentService.getChatHistory({ conversationId: action.payload }),
		doOpenConversationSuccess,
		doOpenConversationFail,
		null, null, ''
	);
}

function* copilotSaga() {
	yield takeLatest('copilot/doAskCopilot', doAskCopilot);
	// ONE watcher, not two. takeLatest keeps a single cancellable task per watcher,
	// so a Try again clicked while a new message was still streaming would run both
	// streams if these were separate. An array pattern cancels across both.
	yield takeLatest(['copilot/doAgentChat', 'copilot/doRetryAgentChat'], doAgentChat);
	yield takeLatest('copilot/doLoadChatHistory', doLoadChatHistory);
	yield takeLatest('copilot/doLoadConversations', doLoadConversationsSaga);
	yield takeLatest('copilot/doOpenConversation', doOpenConversationSaga);
}

export default copilotSaga;
